// <s-tabs> — accessible tabs. Wires ARIA roles, selection, and arrow-key navigation onto a
// [data-tab-trigger] list and matching [data-tab-panel] regions (paired by order). The first
// tab starts active, unless the element has data-collapsible: then every panel starts closed and
// clicking the open tab closes it again. One component per file; DOM work scoped to connectedCallback.
class STabs extends HTMLElement {
  #ac = null;
  #triggers = [];
  #panels = [];
  #collapsible = false;
  #active = -1;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#triggers = [...this.querySelectorAll("[data-tab-trigger]")];
    this.#panels = [...this.querySelectorAll("[data-tab-panel]")];
    if (!this.#triggers.length) return;

    const list = this.querySelector("[data-tabs-list]");
    if (list) list.setAttribute("role", "tablist");

    const base = `s-tabs-${Math.random().toString(36).slice(2, 8)}`;
    this.#triggers.forEach((trigger, i) => {
      const panel = this.#panels[i];
      const tabId = `${base}-tab-${i}`;
      const panelId = `${base}-panel-${i}`;
      trigger.setAttribute("role", "tab");
      trigger.id = tabId;
      if (panel) {
        panel.setAttribute("role", "tabpanel");
        panel.id = panelId;
        panel.setAttribute("aria-labelledby", tabId);
        trigger.setAttribute("aria-controls", panelId);
      }
      trigger.addEventListener("click", () => this.#select(this.#collapsible && this.#active === i ? -1 : i), { signal: this.#ac.signal });
      trigger.addEventListener("keydown", (e) => this.#onKeydown(e, i), { signal: this.#ac.signal });
    });

    this.#collapsible = this.hasAttribute("data-collapsible");
    this.#select(this.#collapsible ? -1 : 0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #select(index) {
    this.#active = index;
    this.#triggers.forEach((trigger, i) => {
      const active = i === index;
      trigger.setAttribute("aria-selected", active ? "true" : "false");
      trigger.tabIndex = active || (index === -1 && i === 0) ? 0 : -1;
      trigger.classList.toggle("border-b-2", active);
      trigger.classList.toggle("border-accent", active);
      trigger.classList.toggle("text-on-surface", active);
      trigger.classList.toggle("text-on-surface-secondary", !active);
      const panel = this.#panels[i];
      if (panel) panel.hidden = !active;
    });
  }

  #onKeydown(event, index) {
    const last = this.#triggers.length - 1;
    let next = null;
    // In right-to-left pages the next tab sits to the left, so the arrow keys swap.
    const rtl = getComputedStyle(this).direction === "rtl";
    const forwardKey = rtl ? "ArrowLeft" : "ArrowRight";
    const backKey = rtl ? "ArrowRight" : "ArrowLeft";
    if (event.key === forwardKey || event.key === "ArrowDown") next = index === last ? 0 : index + 1;
    else if (event.key === backKey || event.key === "ArrowUp") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    if (next === null) return;
    event.preventDefault();
    this.#select(next);
    this.#triggers[next].focus();
  }
}

if (!customElements.get("s-tabs")) {
  customElements.define("s-tabs", STabs);
}
